/**
 * UI Store - Toast notifications, modals, theme
 */
import { defineStore } from 'pinia'
import { ref, watch } from 'vue'

export type ToastType = 'success' | 'error' | 'warning' | 'info'

export interface Toast {
  id: string
  type: ToastType
  title: string
  message?: string
  duration?: number
}

export interface ConfirmDialogOptions {
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  variant?: 'danger' | 'warning' | 'primary'
}

export const useUiStore = defineStore('ui', () => {
  // State
  const toasts = ref<Toast[]>([])
  const isDarkMode = ref(false)
  const isSidebarOpen = ref(true)
  const isMobileSidebarOpen = ref(false)
  const isLoading = ref(false)
  const loadingMessage = ref('')

  // Confirm dialog state
  const confirmDialog = ref<{
    isOpen: boolean
    options: ConfirmDialogOptions | null
    resolve: ((value: boolean) => void) | null
  }>({
    isOpen: false,
    options: null,
    resolve: null,
  })

  // Toast methods
  function showToast(type: ToastType, title: string, message?: string, duration = 5000): void {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const toast: Toast = { id, type, title, message, duration }

    toasts.value.push(toast)

    if (duration > 0) {
      setTimeout(() => {
        removeToast(id)
      }, duration)
    }
  }

  function removeToast(id: string): void {
    const index = toasts.value.findIndex((t) => t.id === id)
    if (index !== -1) {
      toasts.value.splice(index, 1)
    }
  }

  function success(title: string, message?: string): void {
    showToast('success', title, message)
  }

  function error(title: string, message?: string): void {
    showToast('error', title, message, 8000)
  }

  function warning(title: string, message?: string): void {
    showToast('warning', title, message)
  }

  function info(title: string, message?: string): void {
    showToast('info', title, message)
  }

  // Confirm dialog
  function confirm(options: ConfirmDialogOptions): Promise<boolean> {
    return new Promise((resolve) => {
      confirmDialog.value = {
        isOpen: true,
        options,
        resolve,
      }
    })
  }

  function resolveConfirm(value: boolean): void {
    if (confirmDialog.value.resolve) {
      confirmDialog.value.resolve(value)
    }
    confirmDialog.value = {
      isOpen: false,
      options: null,
      resolve: null,
    }
  }

  // Theme
  function toggleDarkMode(): void {
    isDarkMode.value = !isDarkMode.value
    applyTheme()
  }

  function setDarkMode(value: boolean): void {
    isDarkMode.value = value
    applyTheme()
  }

  function applyTheme(): void {
    if (isDarkMode.value) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
    localStorage.setItem('theme', isDarkMode.value ? 'dark' : 'light')
  }

  function initTheme(): void {
    const savedTheme = localStorage.getItem('theme')
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches

    isDarkMode.value = savedTheme ? savedTheme === 'dark' : prefersDark
    applyTheme()

    // Watch for system theme changes
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
      if (!localStorage.getItem('theme')) {
        isDarkMode.value = e.matches
        applyTheme()
      }
    })
  }

  // Sidebar
  function toggleSidebar(): void {
    isSidebarOpen.value = !isSidebarOpen.value
    localStorage.setItem('sidebarOpen', String(isSidebarOpen.value))
  }

  function toggleMobileSidebar(): void {
    isMobileSidebarOpen.value = !isMobileSidebarOpen.value
  }

  function closeMobileSidebar(): void {
    isMobileSidebarOpen.value = false
  }

  function initSidebar(): void {
    const saved = localStorage.getItem('sidebarOpen')
    isSidebarOpen.value = saved !== 'false'
  }

  // Loading
  function startLoading(message = ''): void {
    isLoading.value = true
    loadingMessage.value = message
  }

  function stopLoading(): void {
    isLoading.value = false
    loadingMessage.value = ''
  }

  return {
    // State
    toasts,
    isDarkMode,
    isSidebarOpen,
    isMobileSidebarOpen,
    isLoading,
    loadingMessage,
    confirmDialog,
    // Toast methods
    showToast,
    removeToast,
    success,
    error,
    warning,
    info,
    // Confirm dialog
    confirm,
    resolveConfirm,
    // Theme
    toggleDarkMode,
    setDarkMode,
    initTheme,
    // Sidebar
    toggleSidebar,
    toggleMobileSidebar,
    closeMobileSidebar,
    initSidebar,
    // Loading
    startLoading,
    stopLoading,
  }
})
